/**
 * Mermaid ER Diagram Export utility
 * Exports database model to standard Mermaid Entity Relationship Diagram syntax
 * @see https://mermaid.ai/open-source/syntax/entityRelationshipDiagram.html
 */

/**
 * Escape string for Mermaid attribute comments
 */
const escapeComment = (str) => {
  if (!str) return '';
  // Replace double quotes and line breaks for single line Mermaid comment string
  return str.replace(/"/g, "'").replace(/[\r\n]+/g, ' ').trim();
};

/**
 * Sanitize identifier (entity names, field names) to be valid Mermaid identifiers
 */
const sanitizeIdentifier = (name) => {
  if (!name || typeof name !== 'string') return 'Unnamed';
  // Replace spaces and special characters with underscores, keeping alphanumeric and underscores
  let sanitized = name.trim().replace(/[^a-zA-Z0-9_]/g, '_');
  // Ensure it doesn't start with a number
  if (/^[0-9]/.test(sanitized)) {
    sanitized = `_${sanitized}`;
  }
  return sanitized || 'Unnamed';
};

/**
 * Resolve UDT references to their base type.
 * `UDT:TypeName` -> baseType stored in userTypes array.
 */
const resolveUdt = (type, userTypes = []) => {
  if (!type?.startsWith('UDT:')) return type;
  const udtName = type.slice(4);
  const udt = userTypes.find(u => u.name === udtName);
  return udt ? udt.baseType : udtName;
};

/**
 * Format data type for Mermaid ERD attribute.
 * Removes internal spaces in types (e.g. DOUBLE PRECISION -> DOUBLE_PRECISION)
 */
const formatDataType = (type, userTypes = []) => {
  if (!type) return 'string';
  const resolved = resolveUdt(type, userTypes);
  // Replace spaces within types to ensure parser reads it as single type token
  return resolved.trim().replace(/\s+/g, '_');
};

/**
 * Generate Mermaid ER Diagram string from modelData
 * @param {Object} modelData - Complete model object containing nodes, edges, metadata, etc.
 * @returns {string} Mermaid ER Diagram syntax string
 */
export const exportToMermaid = (modelData, options = {}) => {
  if (!modelData) return 'erDiagram\n    direction TB\n';

  const {
    name = 'Untitled Model',
    type = 'relational',
    description = '',
    businessRules = '',
    nodes = [],
    edges = [],
    userTypes = []
  } = modelData;

  const direction = options.direction || 'TB';

  let mermaid = `erDiagram\n    direction ${direction}\n`;

  // 1. Header & Metadata Comments
  mermaid += `    %% ========================================\n`;
  mermaid += `    %% Model: ${name}\n`;
  mermaid += `    %% Type: ${type}\n`;
  if (description) {
    mermaid += `    %% Description: ${description.replace(/[\r\n]+/g, ' ')}\n`;
  }
  mermaid += `    %% Exported by SchemaGraphos at: ${new Date().toISOString()}\n`;
  mermaid += `    %% ========================================\n\n`;

  // 2. Global Business Rules (as comments)
  if (businessRules && businessRules.trim()) {
    mermaid += `    %% ========================================\n`;
    mermaid += `    %% Global Business Rules\n`;
    mermaid += `    %% ========================================\n`;
    businessRules.trim().split('\n').forEach(line => {
      mermaid += `    %% ${line}\n`;
    });
    mermaid += `\n`;
  }

  // Filter out text box nodes
  const entityNodes = nodes.filter(n => n.type !== 'text');
  const textBoxes = nodes.filter(n => n.type === 'text');

  // Text notes as Mermaid comments
  if (textBoxes.length > 0) {
    mermaid += `    %% ========================================\n`;
    mermaid += `    %% Model Notes\n`;
    mermaid += `    %% ========================================\n`;
    textBoxes.forEach(tb => {
      const content = tb.data?.content || '';
      content.split('\n').forEach(line => {
        mermaid += `    %% ${line}\n`;
      });
    });
    mermaid += `\n`;
  }

  // Create mapping of node.id -> sanitized Entity Name & Display Name
  const entityNameMap = new Map();
  const entityDisplayNameMap = new Map();
  const nameCounts = new Map();

  entityNodes.forEach(node => {
    const rawName = node.data?.name || `Entity_${node.id}`;
    const schema = node.data?.schema?.trim();
    const displayName = schema ? `${schema}.${rawName}` : rawName;
    let sanitized = sanitizeIdentifier(schema ? `${schema}_${rawName}` : rawName);

    // Handle collision if two entities share the same sanitized name
    if (nameCounts.has(sanitized)) {
      const count = nameCounts.get(sanitized) + 1;
      nameCounts.set(sanitized, count);
      sanitized = `${sanitized}_${count}`;
    } else {
      nameCounts.set(sanitized, 1);
    }

    entityNameMap.set(node.id, sanitized);
    entityDisplayNameMap.set(node.id, displayName);
  });

  // 3. Export Entities & Attributes
  mermaid += `    %% ========================================\n`;
  mermaid += `    %% Entities & Attributes\n`;
  mermaid += `    %% ========================================\n\n`;

  entityNodes.forEach(node => {
    const entityName = entityNameMap.get(node.id);
    const displayName = entityDisplayNameMap.get(node.id);
    const entityHeader = node.data?.schema?.trim() ? `${entityName}["${displayName}"]` : entityName;
    const fields = node.data?.fields || [];
    const entityDesc = node.data?.description;
    const nodeType = node.type || 'table';
    const entityRules = node.data?.businessRules?.entity;

    if (entityDesc) {
      mermaid += `    %% ${displayName} (${nodeType}): ${escapeComment(entityDesc)}\n`;
    }

    if (entityRules) {
      mermaid += `    %% Business Rules for ${displayName}:\n`;
      entityRules.split('\n').forEach(ruleLine => {
        mermaid += `    %%   - ${ruleLine}\n`;
      });
    }

    // Partitioning documentation in Mermaid comments
    const partitioning = node.data?.partitioning;
    const isPartitioned = partitioning && partitioning.type && partitioning.type !== 'none';
    const partitionColumns = isPartitioned
      ? (partitioning.type === 'hash'
          ? (partitioning.columns || [])
          : (partitioning.column ? [partitioning.column] : (partitioning.columns || [])))
      : [];

    if (isPartitioned) {
      const colStr = partitionColumns.join(', ') || 'N/A';
      mermaid += `    %% Partitioning for ${displayName}: ${partitioning.type.toUpperCase()} ON (${colStr})\n`;
      if (partitioning.partitions && partitioning.partitions.length > 0) {
        partitioning.partitions.forEach(part => {
          if (partitioning.type === 'range') {
            mermaid += `    %%   - Partition ${part.name || 'unnamed'}: FROM (${part.from || 'MINVALUE'}) TO (${part.to || 'MAXVALUE'})\n`;
          } else if (partitioning.type === 'list') {
            mermaid += `    %%   - Partition ${part.name || 'unnamed'}: VALUES IN (${part.values || 'DEFAULT'})\n`;
          }
        });
      } else if (partitioning.type === 'hash' && partitioning.hashPartitionsCount) {
        mermaid += `    %%   - Hash Buckets Count: ${partitioning.hashPartitionsCount}\n`;
      }
    }

    if (fields.length === 0) {
      mermaid += `    ${entityHeader}\n\n`;
    } else {
      mermaid += `    ${entityHeader} {\n`;
      fields.forEach(field => {
        const fieldName = sanitizeIdentifier(field.name || 'unnamed_field');
        const dataType = formatDataType(field.type, userTypes);
        const isPartitionKey = partitionColumns.includes(field.name);
        
        // Keys: PK, FK, UK
        const keys = [];
        if (field.isPrimaryKey || field.primaryKey || field.isPk) keys.push('PK');
        if (field.isForeignKey || field.foreignKey || field.isFk) keys.push('FK');
        if (field.unique || field.isUnique) keys.push('UK');
        const keyStr = keys.length > 0 ? ` ${keys.join(',')}` : '';

        // Comment
        let commentText = field.description || '';
        if (isPartitionKey) {
          commentText = commentText ? `[PARTITION KEY] ${commentText}` : `[PARTITION KEY]`;
        }
        if (field.checkConstraint) {
          commentText = commentText ? `${commentText} [CHECK: ${field.checkConstraint}]` : `[CHECK: ${field.checkConstraint}]`;
        }
        const commentStr = commentText ? ` "${escapeComment(commentText)}"` : '';

        mermaid += `        ${dataType} ${fieldName}${keyStr}${commentStr}\n`;
      });
      mermaid += `    }\n\n`;
    }
  });

  // 4. Handle Junction Tables (N:N) if defined on edges
  const junctionEdges = [];
  const handledJunctionNames = new Set();

  edges.forEach(edge => {
    const cardinality = edge.data?.cardinality || '1:N';
    const junctionTable = edge.data?.junctionTable;

    if (cardinality === 'N:N' && junctionTable) {
      junctionEdges.push(edge);
      const rawJunctionName = junctionTable.name || 'junction_table';
      const jName = sanitizeIdentifier(rawJunctionName);

      if (!handledJunctionNames.has(jName)) {
        handledJunctionNames.add(jName);
        const jFields = junctionTable.fields || [];

        if (junctionTable.description) {
          mermaid += `    %% Junction Table ${jName}: ${escapeComment(junctionTable.description)}\n`;
        }

        if (jFields.length === 0) {
          mermaid += `    ${jName}\n\n`;
        } else {
          mermaid += `    ${jName} {\n`;
          jFields.forEach(field => {
            const fieldName = sanitizeIdentifier(field.name || 'unnamed_field');
            const dataType = formatDataType(field.type, userTypes);

            const keys = [];
            if (field.isPrimaryKey || field.primaryKey || field.isPk) keys.push('PK');
            if (field.isForeignKey || field.foreignKey || field.isFk) keys.push('FK');
            if (field.unique || field.isUnique) keys.push('UK');
            const keyStr = keys.length > 0 ? ` ${keys.join(',')}` : '';

            const commentStr = field.description ? ` "${escapeComment(field.description)}"` : '';

            mermaid += `        ${dataType} ${fieldName}${keyStr}${commentStr}\n`;
          });
          mermaid += `    }\n\n`;
        }
      }
    }
  });

  // 5. Export Relationships
  mermaid += `    %% ========================================\n`;
  mermaid += `    %% Relationships\n`;
  mermaid += `    %% ========================================\n\n`;

  edges.forEach((edge, index) => {
    const sourceName = entityNameMap.get(edge.source);
    const targetName = entityNameMap.get(edge.target);

    if (!sourceName || !targetName) return;

    const cardinality = edge.data?.cardinality || '1:N';
    const rawLabel = edge.data?.label || edge.label || edge.data?.name || '';
    const label = rawLabel ? escapeComment(rawLabel) : 'relates to';
    const junctionTable = edge.data?.junctionTable;

    // Handle N:N with junction table
    if (cardinality === 'N:N' && junctionTable) {
      const jName = sanitizeIdentifier(junctionTable.name || `junction_${index}`);
      mermaid += `    ${sourceName} ||--|{ ${jName} : "has"\n`;
      mermaid += `    ${targetName} ||--|{ ${jName} : "has"\n`;
      return;
    }

    // Standard cardinalities mapping to Mermaid ERD syntax
    let mermaidRel = '||--|{'; // default 1:N
    switch (cardinality) {
      case '1:1':
        mermaidRel = '||--||';
        break;
      case '1:N':
        mermaidRel = '||--|{';
        break;
      case 'N:1':
        mermaidRel = '}|--||';
        break;
      case 'N:N':
        mermaidRel = '}|--|{';
        break;
      default:
        mermaidRel = '||--|{';
    }

    mermaid += `    ${sourceName} ${mermaidRel} ${targetName} : "${label}"\n`;
  });

  return mermaid;
};

/**
 * Download Mermaid ER Diagram file (.mmd)
 * @param {Object} modelData - Model data object
 * @param {string} filename - Filename for download
 */
export const downloadMermaid = (modelData, filename = 'model.mmd') => {
  const mermaidContent = exportToMermaid(modelData);
  const blob = new Blob([mermaidContent], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
